import { useReveal } from "@/hooks/useReveal";
import { Link } from "react-router-dom";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import SEO from "@/components/site/SEO";
import { Button } from "@/components/ui/button";
import { Linkedin, Mail, MapPin, Sparkles, Heart, Rocket, Handshake } from "lucide-react";
import { CALENDLY_URL } from "@/lib/constants";
import authorIsabelle from "@/assets/author-isabelle.webp";
import authorCharlie from "@/assets/author-charlie.webp";
import authorJann from "@/assets/author-jann.webp";
import aboutTeam from "@/assets/team-hero.jpg";
import finableLogo from "@/assets/finable-mark.svg";

type Member = {
  name: string;
  role: string;
  image: string;
  imagePosition?: string;
  bio: string;
  focus: string[];
  email?: string;
};

const team: Member[] = [
  {
    name: "Isabelle",
    role: "Beraterin Altersvorsorge",
    image: authorIsabelle,
    imagePosition: "object-[20%_center]",
    bio: "Isabelle begleitet unsere Mandant:innen auf dem Weg in einen finanziell sorgenfreien Ruhestand. Mit ruhiger Hand und einem klaren Blick für staatliche Förderungen baut sie Vorsorgekonzepte, die langfristig tragen.",
    focus: ["Private & betriebliche Altersvorsorge", "Rürup, Riester & geförderte Konzepte", "Ruhestandsplanung"],
    email: "moin@finable.de",
  },
  {
    name: "Charlie",
    role: "Berater Investments",
    image: authorCharlie,
    imagePosition: "object-[75%_center]",
    bio: "Charlie denkt in Portfolios statt in Produkten. Er übersetzt Kapitalmärkte in verständliche Strategien und sorgt dafür, dass Dein Vermögen zu Deinen Zielen passt – nicht andersherum.",
    focus: ["ETF- & Fondsstrategien", "Vermögensaufbau & Sparpläne", "Risikomanagement"],
    email: "moin@finable.de",
  },
  {
    name: "Jann",
    role: "Berater Vermögensaufbau",
    image: authorJann,
    imagePosition: "object-[center_25%]",
    bio: "Jann steht für strukturierten Vermögensaufbau – ehrlich, transparent und immer auf Deine Lebensphase abgestimmt. Sein Anspruch: Finanzen, die Du wirklich verstehst und die zu Dir passen.",
    focus: ["Ganzheitliche Finanzplanung", "Immobilien als Kapitalanlage", "Steueroptimierung"],
    email: "moin@finable.de",
  },
];

const values = [
  { icon: Sparkles, title: "Exzellenz", text: "Höchste Beratungsqualität in jedem Detail – präzise, verlässlich, auf den Punkt." },
  { icon: Heart, title: "Vertrautheit", text: "Wir hören zu, bevor wir empfehlen. Beratung auf Augenhöhe, ohne Verkaufsdruck." },
  { icon: Rocket, title: "Fortschritt", text: "Digital, modern, durchdacht. Wir denken Finanzberatung neu – und entwickeln uns laufend weiter." },
  { icon: Handshake, title: "Treue", text: "Wir begleiten Dich langfristig. Verbindlich, loyal und mit echtem Interesse an Deinem Erfolg." },
];

export default function Team() {
  useReveal();

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Unser Team – FINABLE Finanzberatung Hamburg"
        description="Lerne das Team von FINABLE kennen: unabhängige Berater:innen für Altersvorsorge, Investments und Vermögensaufbau."
        canonical="https://finable.de/team"
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 md:pt-40 pb-20 md:pb-28 bg-gradient-hero text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial pointer-events-none" />
          <div className="absolute top-1/4 -right-20 w-96 h-96 bg-accent/20 rounded-full blur-3xl" />
          <div className="container relative grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="text-center lg:text-left">
              <span className="reveal inline-block text-accent font-semibold uppercase tracking-wider text-sm">
                Unser Team
              </span>
              <h1 className="reveal mt-4 font-display text-4xl md:text-6xl lg:text-6xl xl:text-7xl font-extrabold text-balance leading-[1.05]">
                Menschen,<br />die <span className="text-accent">Finanzen</span><br />menschlich machen.
              </h1>
              <p className="reveal mt-8 text-lg md:text-xl text-white/80 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                Wir sind kein anonymes Callcenter und kein Produktverkauf. Wir sind ein kleines, schlagkräftiges Team aus Hamburg – mit echter Expertise und einem ehrlichen Anspruch: Finanzberatung, die Du verstehst und der Du vertrauen kannst.
              </p>
              <div className="reveal mt-10 flex flex-wrap gap-4 justify-center lg:justify-start">
                <Button asChild size="lg" variant="secondary" className="text-base px-8 py-6 h-auto">
                  <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">Erstgespräch buchen</a>
                </Button>
              </div>
            </div>
            <div className="reveal relative hidden lg:block">
              <div className="absolute -inset-4 bg-accent/20 rounded-3xl blur-2xl" aria-hidden />
              <div className="relative aspect-[4/5] lg:aspect-[5/6] rounded-3xl overflow-hidden shadow-elegant ring-1 ring-white/10">
                <img src={aboutTeam} alt="Das FINABLE Team" className="w-full h-full object-cover" loading="eager" />
              </div>
            </div>
          </div>
        </section>

        {/* Team grid */}
        <section className="py-20 md:py-28 bg-background">
          <div className="container">
            <div className="text-center max-w-3xl mx-auto reveal">
              <span className="text-primary font-semibold uppercase tracking-wider text-sm">Deine Ansprechpartner</span>
              <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-balance">
                Das ist FINABLE
              </h2>
              <p className="mt-5 text-lg text-muted-foreground">
                Drei Berater:innen, ein gemeinsamer Anspruch: unabhängige Beratung, die wirklich zu Dir passt.
              </p>
            </div>

            <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {team.map((m) => (
                <article
                  key={m.name}
                  className="reveal group bg-card border border-border rounded-3xl overflow-hidden hover:shadow-elegant hover:-translate-y-1 transition-all duration-300 flex flex-col"
                >
                  <div className="aspect-[4/5] overflow-hidden bg-muted">
                    <img
                      src={m.image}
                      alt={`${m.name} – ${m.role}`}
                      loading="lazy"
                      className={`w-full h-full object-cover ${m.imagePosition ?? "object-center"} group-hover:scale-105 transition-transform duration-500`}
                    />
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <h3 className="font-display text-2xl font-bold">{m.name}</h3>
                    <div className="text-sm font-semibold text-accent mt-1">{m.role}</div>
                    <p className="mt-4 flex-1 text-muted-foreground leading-relaxed">{m.bio}</p>

                    <div className="mt-5 pb-5">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-primary mb-2">Schwerpunkte</div>
                      <ul className="space-y-1.5">
                        {m.focus.map((f) => (
                          <li key={f} className="text-sm text-foreground/80 flex items-start gap-2">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {m.email && (
                      <div className="mt-auto pt-5 border-t border-border flex items-center gap-4 flex-wrap">
                        <a
                          href={`mailto:${m.email}`}
                          className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-accent transition-colors"
                        >
                          <Mail className="w-4 h-4" />
                          Kontakt
                        </a>
                        {m.name === "Jann" && (
                          <Link
                            to="/team/jann-kromminga"
                            className="inline-flex items-center gap-1 text-sm font-semibold text-accent hover:underline ml-auto"
                          >
                            Mehr über Jann →
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 md:py-28 bg-muted/30 overflow-hidden">
          <div className="container">
            <div className="text-center max-w-3xl mx-auto reveal">
              <span className="text-primary font-semibold uppercase tracking-wider text-sm">Wofür wir stehen</span>
              <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-balance">
                Unsere <span className="text-accent">Werte</span>
              </h2>
              <p className="mt-5 text-lg text-muted-foreground">
                Vier Prinzipien, die unser tägliches Handeln prägen – in jedem Gespräch, in jedem Konzept.
              </p>
            </div>

            {/* Desktop / Tablet: Orbit layout with scribble */}
            <div className="hidden md:block relative mt-20 mx-auto" style={{ maxWidth: 880, height: 720 }}>
              {/* Animated scribble around the logo – continuously swinging */}
              <svg
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none text-accent/55"
                width="360"
                height="360"
                viewBox="0 0 320 320"
                fill="none"
                aria-hidden
              >
                <ellipse cx="160" cy="160" rx="118" ry="108" stroke="currentColor" strokeWidth="1.2" className="scribble-swing" style={{ animationDuration: "9s", animationDelay: "0s" }} />
                <ellipse cx="160" cy="160" rx="108" ry="120" stroke="currentColor" strokeWidth="1.2" className="scribble-swing-rev" style={{ animationDuration: "11s", animationDelay: "-2s" }} />
                <ellipse cx="160" cy="160" rx="122" ry="114" stroke="currentColor" strokeWidth="1" className="scribble-swing" style={{ animationDuration: "13s", animationDelay: "-4s" }} />
                <ellipse cx="160" cy="160" rx="112" ry="122" stroke="currentColor" strokeWidth="0.9" className="scribble-swing-rev" style={{ animationDuration: "15s", animationDelay: "-6s" }} />
              </svg>

              {/* Center brand mark – no background, centered in the rings */}
              <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
                <img src={finableLogo} alt="FINABLE Finanzberatung Logo" className="block w-24 h-24 object-contain" />
              </div>

              {/* Value circles around the logo */}
              <div className="absolute inset-0">
                {values.map(({ icon: Icon, title, text }, idx) => {
                  const angle = (idx * 90) - 45; // diagonals
                  const radius = 320; // px from center – pushed outward so text clears the rings
                  const rad = (angle * Math.PI) / 180;
                  const x = Math.cos(rad) * radius;
                  const y = Math.sin(rad) * radius;
                  return (
                    <div
                      key={title}
                      className="absolute left-1/2 top-1/2 reveal"
                      style={{
                        transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                        animationDelay: `${idx * 120}ms`,
                      } as React.CSSProperties}
                    >
                      <div className="flex flex-col items-center w-44 group">
                        <div className="w-20 h-20 rounded-full border-[3px] border-accent bg-card flex items-center justify-center text-accent shadow-md group-hover:scale-110 group-hover:shadow-elegant transition-all duration-300">
                          <Icon className="w-9 h-9" strokeWidth={1.5} />
                        </div>
                        <h3 className="mt-3 font-display text-base font-bold text-primary uppercase tracking-wide">{title}</h3>
                        <p className="mt-1 text-xs text-muted-foreground leading-relaxed text-center px-2">{text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>


            {/* Mobile: simple stacked grid */}
            <div className="md:hidden mt-12 grid grid-cols-2 gap-6">
              {values.map(({ icon: Icon, title, text }) => (
                <div key={title} className="reveal flex flex-col items-center text-center">
                  <div className="w-20 h-20 rounded-full border-[3px] border-accent bg-card flex items-center justify-center text-accent shadow-md">
                    <Icon className="w-8 h-8" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-3 font-display text-base font-bold text-primary uppercase tracking-wide">{title}</h3>
                  <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Location / CTA */}
        <section className="py-20 md:py-28 bg-background">
          <div className="container">
            <div className="bg-gradient-hero text-white rounded-3xl p-10 md:p-16 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-80 h-80 bg-accent/20 rounded-full blur-3xl" />
              <div className="relative grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 text-accent font-semibold uppercase tracking-wider text-sm">
                    <MapPin className="w-4 h-4" />
                    Hamburg · bundesweit digital
                  </div>
                  <h2 className="mt-4 font-display text-3xl md:text-5xl font-bold text-balance">
                    Lerne uns kennen.
                  </h2>
                  <p className="mt-5 text-lg text-white/80 leading-relaxed">
                    Ein kostenloses Erstgespräch – unverbindlich, persönlich und auf Augenhöhe. Online oder bei uns in der Rothenbaumchaussee.
                  </p>
                </div>
                <div className="flex flex-wrap gap-4 lg:justify-end">
                  <Button asChild size="lg" variant="secondary" className="text-base px-8 py-6 h-auto">
                    <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">Termin vereinbaren</a>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="text-base px-8 py-6 h-auto bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white">
                    <a href="mailto:moin@finable.de">E-Mail schreiben</a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
